import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { grammarPoints } from "../consts/grammarPoints";
import type { TableCellValue } from "../components/ui/TableCell/TableCell";
import { localizePath } from "./localizedPath";
import ptGrammarContent from "./locales/pt/grammarContent.json";

type GrammarExample = { japanese: string; reading: string; english: string };

// A reference table shown between "Formation" and "Examples" - used where a
// pattern is clearer as a grid than as prose (e.g. the five verb steps).
export type GrammarTable = { caption: string; headers: string[]; rows: TableCellValue[][] };

// A "see also" link to another surface (lesson, blog post, conjugation page).
// `to` is an app path; localizeGrammarPoint resolves it to the final href for the
// language, unless a translation overrides it (standalone PT-PT blog posts live
// under /blog/, not /pt/blog/).
export type GrammarRelatedLink = { to: string; label: string };

type GrammarPoint = {
    id: string;
    pattern: string;
    jlptLevel: number | null;
    category?: string;
    title: string;
    meaning: string;
    explanation: string;
    formation: string;
    examples: GrammarExample[];
    tables?: GrammarTable[];
    related?: GrammarRelatedLink[];
};

export type GrammarPointTranslation = {
    pattern?: string;
    title: string;
    meaning: string;
    explanation: string;
    formation: string;
    examples: string[];
    tables?: GrammarTable[];
    related?: Array<{ to?: string; label: string }>;
};

export type LocalizedGrammarExample = GrammarExample & { translation: string };

export type LocalizedGrammarPoint = Omit<GrammarPoint, "examples"> & { examples: LocalizedGrammarExample[] };

const ptTranslations = (ptGrammarContent as unknown as { points: Record<string, GrammarPointTranslation> }).points;

export function localizeGrammarPoint(
    point: GrammarPoint,
    translation?: GrammarPointTranslation | null,
    language = "en",
): LocalizedGrammarPoint {
    return {
        ...point,
        pattern: translation?.pattern ?? point.pattern,
        title: translation?.title ?? point.title,
        meaning: translation?.meaning ?? point.meaning,
        explanation: translation?.explanation ?? point.explanation,
        formation: translation?.formation ?? point.formation,
        examples: point.examples.map((example, index) => ({
            ...example,
            translation: translation?.examples?.[index] ?? example.english,
        })),
        tables: translation?.tables ?? point.tables,
        related: point.related?.map((link, index) => ({
            to: translation?.related?.[index]?.to ?? localizePath(link.to, language),
            label: translation?.related?.[index]?.label ?? link.label,
        })),
    };
}

export function getGrammarPoints(language: string): LocalizedGrammarPoint[] {
    const translations = language === "pt" ? ptTranslations : null;
    return (grammarPoints as GrammarPoint[]).map((point) =>
        localizeGrammarPoint(point, translations?.[point.id], language),
    );
}

export function useGrammarPoints(): LocalizedGrammarPoint[] {
    const { i18n } = useTranslation();
    const language = i18n.language === "pt" ? "pt" : "en";
    return useMemo(() => getGrammarPoints(language), [language]);
}
